import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { MagnusLogo } from "./Logo";
import { NAV_LINKS, scrollToId, waLink, DEFAULT_WA_MSG } from "../lib/site";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 250 : 0);
    };

    return (
        <>
            <motion.header
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "backdrop-blur-md bg-cream/85 border-b border-gold/20 py-2.5"
                        : "bg-transparent py-4"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
                    <button
                        data-testid="nav-logo-button"
                        onClick={() => go("inicio")}
                        className="shrink-0"
                        aria-label="Magnus Personalizados — Início"
                    >
                        <MagnusLogo />
                    </button>

                    <nav className="hidden lg:flex items-center gap-6">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="font-body text-[13px] font-medium text-ink/75 hover:text-gold-deep transition-colors tracking-wide"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            data-testid="nav-whatsapp-cta"
                            href={waLink(DEFAULT_WA_MSG)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden sm:inline-flex items-center gap-2 bg-obsidian text-cream font-body text-[11px] font-semibold uppercase tracking-[0.14em] px-5 py-3 rounded-full hover:bg-gold-deep transition-colors duration-300"
                        >
                            Fale conosco pelo WhatsApp
                        </a>
                        <button
                            data-testid="mobile-menu-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-gold/40 text-ink"
                            aria-label="Abrir menu"
                        >
                            {open ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 bg-cream/98 backdrop-blur-lg pt-28 px-6 pb-10 flex flex-col"
                    >
                        <nav className="flex flex-col gap-1 overflow-y-auto">
                            {NAV_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                    onClick={() => go(l.id)}
                                    className="text-left font-display text-3xl text-ink py-3 border-b border-gold/15 hover:text-gold-deep transition-colors"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                        </nav>
                        <a
                            data-testid="mobile-menu-whatsapp-cta"
                            href={waLink(DEFAULT_WA_MSG)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-auto inline-flex items-center justify-center gap-2 bg-obsidian text-cream font-body text-xs font-semibold uppercase tracking-[0.14em] px-6 py-4 rounded-full"
                        >
                            Fale conosco pelo WhatsApp
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
